import * as React from "react";
import { Camera, Loader2, Trash2 } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "./avatar";
import { cn } from "../lib/utils";

/**
 * AvatarUpload - profile photo picker/uploader, shared between web_admin + web_account
 * (both talk to the same /api/me/avatar endpoint). Presentational:
 * API calls come in as `onUpload`/`onRemove` callbacks (the apps have different
 * fetch wrappers) and labels as the `labels` prop (i18n lives in each app's messages
 * JSON files, not in the shared package).
 *
 * Error display: the `Error.message` thrown by the callback is matched against the API
 * error codes (both apps' ApiError puts `body.error` into the message) → translated label.
 */

export interface AvatarUploadLabels {
  change: string;
  remove: string;
  errorGeneric: string;
  errorTooLarge: string;
  errorType: string;
}

/** Mirrors the API side: allowed MIME types (lib/uploads ALLOWED_IMAGE_MIME) + 8MB multipart limit. */
const ACCEPT_MIME = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/avif", "image/svg+xml"];
const MAX_BYTES = 8 * 1024 * 1024;

export function AvatarUpload({
  src,
  fallback,
  labels,
  onUpload,
  onRemove,
  size = "size-16",
  className,
  fallbackClassName,
  inputId,
}: {
  /** Current photo URL; when missing, the initials are shown. */
  src?: string | null;
  /** Initials for the fallback (computed by the caller - the name/email logic belongs to the app). */
  fallback: string;
  labels: AvatarUploadLabels;
  onUpload: (file: File) => Promise<void>;
  onRemove?: () => Promise<void>;
  /** Tailwind size class for the avatar circle (e.g. "size-16", "size-10"). */
  size?: string;
  className?: string;
  /** Style of the initials circle - pass the same as the surrounding view, so the fallback
   *  does not look different next to another avatar on the same page. */
  fallbackClassName?: string;
  /** id for the hidden file input - lets an outside <Label htmlFor> open the picker. */
  inputId?: string;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  function errorLabel(e: unknown): string {
    // 413 can also come from above the API (e.g. the nginx body limit) without a JSON body -
    // then there is no error code, but the status says the same thing.
    if ((e as { status?: number } | null)?.status === 413) return labels.errorTooLarge;
    const code = e instanceof Error ? e.message : "";
    if (code === "file_too_large") return labels.errorTooLarge;
    if (code === "unsupported_type" || code === "invalid_image") return labels.errorType;
    return labels.errorGeneric;
  }

  async function run(action: () => Promise<void>): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await action();
    } catch (e) {
      setError(errorLabel(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleFile(file: File | null | undefined): Promise<void> {
    if (inputRef.current) inputRef.current.value = ""; // picking the same file again must fire
    if (!file) return;
    // Client-side pre-check - the same rule as on the server, but without a network round trip.
    if (!ACCEPT_MIME.includes(file.type)) return void setError(labels.errorType);
    if (file.size > MAX_BYTES) return void setError(labels.errorTooLarge);
    await run(() => onUpload(file));
  }

  return (
    <div className={cn("flex items-center gap-3", className)}>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={ACCEPT_MIME.join(",")}
        className="hidden"
        onChange={(e) => void handleFile(e.target.files?.[0])}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        aria-label={labels.change}
        title={labels.change}
        className={cn("group relative shrink-0 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring", size)}
      >
        <Avatar className="size-full">
          {src ? <AvatarImage src={src} alt="" /> : null}
          <AvatarFallback className={cn("uppercase", fallbackClassName)}>{fallback}</AvatarFallback>
        </Avatar>
        <span
          className={cn(
            "absolute inset-0 flex items-center justify-center rounded-full bg-black/45 text-white transition-opacity",
            busy ? "opacity-100" : "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100",
          )}
        >
          {busy ? <Loader2 className="size-1/3 animate-spin" /> : <Camera className="size-1/3" />}
        </span>
      </button>
      <div className="min-w-0 space-y-0.5">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="block text-xs font-medium text-primary hover:underline disabled:opacity-50"
        >
          {labels.change}
        </button>
        {src && onRemove && (
          <button
            type="button"
            onClick={() => void run(onRemove)}
            disabled={busy}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive disabled:opacity-50"
          >
            <Trash2 className="size-3" /> {labels.remove}
          </button>
        )}
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    </div>
  );
}
